import type { ActionType, EventType } from "../actions";
import { COMPARE_OPS } from "../compareRegistry";
import { GET_REGISTRY } from "../getRegistry";
import { MATH_OPS } from "../mathRegistry";
import type { DataPort, FieldRequirement, SelectionInput, EventSpec } from "../schemaSpec";
import { EVENT_SPEC_BY_TYPE } from "../schemaSpec";
import {
	HANDLE_NEXT,
	HANDLE_SUCCESS,
	HANDLE_FAILED,
	HANDLE_CANCELLED,
	HANDLE_IN_PROGRESS,
	HANDLE_SUCCEEDED,
	HANDLE_ERROR,
	HANDLE_NOT_STARTED,
	HANDLE_TRUE,
	HANDLE_FALSE,
	HANDLE_ARRIVED,
	HANDLE_NO_WAY,
	HANDLE_ON_START,
	HANDLE_ON_END,
	HANDLE_ON_EVENT,
	NEXT_ONLY,
} from "./handles";
import { UI_PROPERTY_KEYS } from "../uiPropertyRegistry";

const VALUE_PORT_NUMBER: readonly DataPort[] = [{ name: "value", kind: "number" }];
const VALUE_PORT_STRING: readonly DataPort[] = [{ name: "value", kind: "string" }];

const GET_OUTPUT_NAMES = Array.from(
    new Set(GET_REGISTRY.flatMap((s) => s.outputs.map((o) => o.name))),
);

const GET_OUTPUTS_ALL: readonly DataPort[] = GET_OUTPUT_NAMES.map((name) => ({
    name,

    kind: "any" as const,
}));

export type ActionSpec = {
    type: ActionType;
    fields: FieldRequirement[];
    outputHandles: readonly string[];
    isBlocking: boolean;
    contextRequiresPlayer: boolean;
    contextRequiresEntity: boolean;
    skippedWhenMissingContext: boolean;

    dataInputs?: readonly DataPort[];
    dataOutputs?: readonly DataPort[];
    selectionInputs?: readonly SelectionInput[];
};

export const ACTION_SPECS: ActionSpec[] = [
    {
        type: "ModifyStat",
        fields: [
            { name: "mode", kind: "string", required: true },
            { name: "statKey", kind: "string", required: true, refEntity: "stats" },
            { name: "value", kind: "string", required: true },
            { name: "target", kind: "string", required: false, minLength: 1 },
        ],
        outputHandles: [HANDLE_SUCCESS, HANDLE_FAILED],
        isBlocking: false,
        contextRequiresPlayer: false,
        contextRequiresEntity: false,
        skippedWhenMissingContext: false,
    },
    {
        type: "ModifyCurrency",
        fields: [
            { name: "mode", kind: "string", required: true },
            { name: "currencyId", kind: "string", required: true, refEntity: "currencies" },
            { name: "value", kind: "string", required: true },
            { name: "target", kind: "string", required: false, minLength: 1 },
        ],
        outputHandles: [HANDLE_SUCCESS, HANDLE_FAILED],
        isBlocking: false,
        contextRequiresPlayer: false,
        contextRequiresEntity: false,
        skippedWhenMissingContext: false,
    },
    {
        type: "SetTimer",
        fields: [
            { name: "duration", kind: "number", required: true, gt: 0 },
            { name: "label", kind: "string", required: false },
            { name: "skipCurrencyId", kind: "string", required: false, refEntity: "currencies" },
            { name: "skipAmount", kind: "number", required: false, gt: 0 },
        ],
        outputHandles: NEXT_ONLY,
        isBlocking: false,
        contextRequiresPlayer: false,
        contextRequiresEntity: false,
        skippedWhenMissingContext: false,
    },
    {
        type: "ModifyItem",
        fields: [
            { name: "mode", kind: "string", required: true },
            { name: "itemId", kind: "string", required: true, refEntity: "items" },
            { name: "quantity", kind: "number", required: true, min: 0 },
            { name: "spawnDrop", kind: "boolean", required: false },
        ],
        outputHandles: [HANDLE_SUCCESS, HANDLE_FAILED],
        isBlocking: false,
        contextRequiresPlayer: true,
        contextRequiresEntity: false,
        skippedWhenMissingContext: true,
    },
    {
        type: "AddRandomItem",
        fields: [{ name: "entries", kind: "array", required: true, minLength: 1 }],
        outputHandles: [HANDLE_SUCCESS, HANDLE_FAILED],
        isBlocking: false,
        contextRequiresPlayer: true,
        contextRequiresEntity: false,
        skippedWhenMissingContext: true,
    },
    {
        type: "ExecuteTransaction",
        fields: [{ name: "recipeId", kind: "string", required: true, refEntity: "recipes" }],
        outputHandles: [HANDLE_SUCCESS, HANDLE_FAILED],
        isBlocking: false,
        contextRequiresPlayer: true,
        contextRequiresEntity: false,
        skippedWhenMissingContext: true,
        dataInputs: [{ name: "recipeId", kind: "string" }],
        selectionInputs: [{ name: "recipeId", kind: "string" }],
    },
    {
        type: "ConsumeItem",
        fields: [],
        outputHandles: [HANDLE_SUCCESS, HANDLE_FAILED],
        isBlocking: false,
        contextRequiresPlayer: true,
        contextRequiresEntity: false,
        skippedWhenMissingContext: false,
    },
    {
        type: "ToggleEquip",
        fields: [],
        outputHandles: [HANDLE_SUCCESS, HANDLE_FAILED],
        isBlocking: false,
        contextRequiresPlayer: true,
        contextRequiresEntity: false,
        skippedWhenMissingContext: false,
    },
    {
        type: "DeployItem",
        fields: [
            { name: "target", kind: "string", required: false, minLength: 1 },
        ],
        outputHandles: [HANDLE_SUCCESS, HANDLE_FAILED],
        isBlocking: false,
        contextRequiresPlayer: true,
        contextRequiresEntity: false,
        skippedWhenMissingContext: false,
    },
    {
        type: "RecallItem",
        fields: [
            { name: "target", kind: "string", required: false, minLength: 1 },
        ],
        outputHandles: [HANDLE_SUCCESS, HANDLE_FAILED],
        isBlocking: false,
        contextRequiresPlayer: false,
        contextRequiresEntity: false,
        skippedWhenMissingContext: false,
    },
    {
        type: "PlaceItem",
        fields: [
            { name: "target", kind: "string", required: false, minLength: 1 },
            { name: "filter", kind: "string", required: false, minLength: 1 },
            { name: "recipes", kind: "array", required: true, minLength: 1 },
        ],
        outputHandles: [HANDLE_SUCCESS, HANDLE_CANCELLED, HANDLE_FAILED],
        isBlocking: true,
        contextRequiresPlayer: true,
        contextRequiresEntity: false,
        skippedWhenMissingContext: true,
    },
    {
        type: "ClaimPlace",
        fields: [
            { name: "placeGroupId", kind: "string", required: true, minLength: 1 },
        ],
        outputHandles: [HANDLE_SUCCESS, HANDLE_FAILED],
        isBlocking: false,
        contextRequiresPlayer: true,
        contextRequiresEntity: false,
        skippedWhenMissingContext: true,
        dataInputs: [{ name: "owners", kind: "list" }],
    },
    {
        type: "GoToZone",
        fields: [
            { name: "zoneItemId", kind: "string", required: true, refEntity: "items" },
            { name: "placeGroupId", kind: "string", required: false, minLength: 1 },
            { name: "target", kind: "string", required: false, minLength: 1 },
        ],
        outputHandles: [HANDLE_ARRIVED, HANDLE_NO_WAY, HANDLE_FAILED],
        isBlocking: true,
        contextRequiresPlayer: false,
        contextRequiresEntity: false,
        skippedWhenMissingContext: false,
    },
    {
        type: "GoToItem",
        fields: [
            { name: "itemId", kind: "string", required: true, refEntity: "items" },
            { name: "placeGroupId", kind: "string", required: false, minLength: 1 },
            { name: "target", kind: "string", required: false, minLength: 1 },
        ],
        outputHandles: [HANDLE_ARRIVED, HANDLE_NO_WAY, HANDLE_FAILED],
        isBlocking: true,
        contextRequiresPlayer: false,
        contextRequiresEntity: false,
        skippedWhenMissingContext: false,
    },
    {
        type: "LeaveZone",
        fields: [
            { name: "target", kind: "string", required: false, minLength: 1 },
        ],
        outputHandles: [HANDLE_SUCCESS, HANDLE_FAILED],
        isBlocking: false,
        contextRequiresPlayer: false,
        contextRequiresEntity: false,
        skippedWhenMissingContext: false,
    },
    {
        type: "StartProduction",
        fields: [
            { name: "target", kind: "string", required: false, minLength: 1 },
            { name: "outputs", kind: "array", required: true, minLength: 1 },
        ],
        outputHandles: [HANDLE_SUCCESS, HANDLE_FAILED],
        isBlocking: false,
        contextRequiresPlayer: false,
        contextRequiresEntity: false,
        skippedWhenMissingContext: false,
    },
    {
        type: "StopProduction",
        fields: [
            { name: "target", kind: "string", required: false, minLength: 1 },
        ],
        outputHandles: [HANDLE_SUCCESS, HANDLE_FAILED],
        isBlocking: false,
        contextRequiresPlayer: false,
        contextRequiresEntity: false,
        skippedWhenMissingContext: false,
    },
    {
        type: "CollectProduction",
        fields: [
            { name: "mode", kind: "enum", required: true, values: ["Self", "All", "Target"] as const },
            { name: "target", kind: "string", required: false, minLength: 1 },
        ],
        outputHandles: [HANDLE_SUCCESS, HANDLE_FAILED],
        isBlocking: false,
        contextRequiresPlayer: true,
        contextRequiresEntity: false,
        skippedWhenMissingContext: true,
    },
    {
        type: "Despawn",
        fields: [],
        outputHandles: NEXT_ONLY,
        isBlocking: false,
        contextRequiresPlayer: false,
        contextRequiresEntity: true,
        skippedWhenMissingContext: true,
    },
    {
        type: "Stop",
        fields: [],
        outputHandles: [],
        isBlocking: false,
        contextRequiresPlayer: false,
        contextRequiresEntity: true,
        skippedWhenMissingContext: true,
    },
    {
        type: "Transform",
        fields: [
            { name: "recipes", kind: "array", required: true, minLength: 1 },
            { name: "title", kind: "string", required: true, minLength: 1 },
        ],
        outputHandles: [HANDLE_SUCCESS, HANDLE_CANCELLED],
        isBlocking: true,
        contextRequiresPlayer: true,
        contextRequiresEntity: false,
        skippedWhenMissingContext: true,
    },
    {
        type: "Teleport",
        fields: [
            { name: "destination", kind: "string", required: false, minLength: 1 },
        ],
        outputHandles: [HANDLE_SUCCESS, HANDLE_FAILED],
        isBlocking: false,
        contextRequiresPlayer: true,
        contextRequiresEntity: false,
        skippedWhenMissingContext: false,
    },
    {
        type: "CreateTeam",
        fields: [
            { name: "maxSize", kind: "number", required: true, gt: 0 },
        ],
        outputHandles: [HANDLE_SUCCESS, HANDLE_FAILED],
        isBlocking: false,
        contextRequiresPlayer: true,
        contextRequiresEntity: false,
        skippedWhenMissingContext: true,
    },
    {
        type: "LeaveTeam",
        fields: [],
        outputHandles: [HANDLE_SUCCESS, HANDLE_FAILED],
        isBlocking: false,
        contextRequiresPlayer: true,
        contextRequiresEntity: false,
        skippedWhenMissingContext: true,
    },
    {
        type: "InvitePlayerToTeam",
        fields: [
            { name: "targetUserId", kind: "number", required: false },
            { name: "maxSize", kind: "number", required: false, gt: 0 },
        ],
        outputHandles: [HANDLE_SUCCESS, HANDLE_FAILED],
        isBlocking: false,
        contextRequiresPlayer: true,
        contextRequiresEntity: false,
        skippedWhenMissingContext: true,
        dataInputs: [{ name: "targetUserId", kind: "number" }],
        selectionInputs: [{ name: "targetUserId", kind: "integer" }],
    },
    {
        type: "AcceptTeamInvite",
        fields: [
            { name: "inviteId", kind: "string", required: false, minLength: 1 },
        ],
        outputHandles: [HANDLE_SUCCESS, HANDLE_FAILED],
        isBlocking: false,
        contextRequiresPlayer: true,
        contextRequiresEntity: false,
        skippedWhenMissingContext: true,
        dataInputs: [{ name: "inviteId", kind: "string" }],
        selectionInputs: [{ name: "inviteId", kind: "string", maxLength: 64 }],
    },
    {
        type: "DeclineTeamInvite",
        fields: [
            { name: "inviteId", kind: "string", required: false, minLength: 1 },
        ],
        outputHandles: [HANDLE_SUCCESS, HANDLE_FAILED],
        isBlocking: false,
        contextRequiresPlayer: true,
        contextRequiresEntity: false,
        skippedWhenMissingContext: true,
        dataInputs: [{ name: "inviteId", kind: "string" }],
        selectionInputs: [{ name: "inviteId", kind: "string", maxLength: 64 }],
    },
    {
        type: "OnProximity",
        fields: [
            { name: "radius", kind: "number", required: true, min: 0 },
            { name: "targetKind", kind: "enum", required: false, values: ["Players", "Items", "All"] },
            { name: "relationship", kind: "enum", required: false, values: ["Any", "Owner", "NonOwner"] },
            { name: "itemIds", kind: "array", required: false },
        ],
        outputHandles: ["Next", "OnEnter", "OnExit"],
        isBlocking: false,
        contextRequiresPlayer: false,
        contextRequiresEntity: true,
        skippedWhenMissingContext: false,
    },
    {
        type: "ChangeBehavior",
        fields: [
            { name: "behaviorType", kind: "enum", required: true, values: ["Static", "Move", "Protect", "Aggressive"] },
            { name: "targetItemId", kind: "string", required: false, refEntity: "items" },
            { name: "targetItemIds", kind: "array", required: false },
        ],
        outputHandles: NEXT_ONLY,
        isBlocking: false,
        contextRequiresPlayer: false,
        contextRequiresEntity: true,
        skippedWhenMissingContext: false,
    },
    {
        type: "Interact",
        fields: [
            { name: "label", kind: "string", required: true, minLength: 1 },
            { name: "holdDuration", kind: "number", required: false, min: 0 },
            { name: "access", kind: "enum", required: false, values: ["Any", "Owner", "NonOwner"] },
        ],
        outputHandles: NEXT_ONLY,
        isBlocking: true,
        contextRequiresPlayer: false,
        contextRequiresEntity: true,
        skippedWhenMissingContext: true,
    },
    {
        type: "Dialogue",
        fields: [
            { name: "lines", kind: "array", required: true, minLength: 1 },
        ],
        outputHandles: NEXT_ONLY,
        isBlocking: true,
        contextRequiresPlayer: true,
        contextRequiresEntity: false,
        skippedWhenMissingContext: true,
    },
    {
        type: "Spawn",
        fields: [
            { name: "spawnType", kind: "enum", required: true, values: ["respawn", "once"] as const },
            { name: "maxActive", kind: "number", required: true, gt: 0 },
            { name: "interval", kind: "number", required: true, min: 0 },
            {
                name: "contentList",
                kind: "array",
                required: true,
                minLength: 1,
                description: "Array of item ids to spawn, e.g. ['wood', 'slime'].",
            },
        ],
        outputHandles: NEXT_ONLY,
        isBlocking: false,
        contextRequiresPlayer: false,
        contextRequiresEntity: true,
        skippedWhenMissingContext: true,
    },
    {
        type: "Notify",
        fields: [
            { name: "notifType", kind: "string", required: true, minLength: 1 },
            { name: "message", kind: "string", required: true, minLength: 1 },
        ],
        outputHandles: NEXT_ONLY,
        isBlocking: false,
        contextRequiresPlayer: false,
        contextRequiresEntity: true,
        skippedWhenMissingContext: true,
    },
    {
        type: "SetAmbiance",
        fields: [{ name: "preset", kind: "string", required: true, minLength: 1 }],
        outputHandles: NEXT_ONLY,
        isBlocking: false,
        contextRequiresPlayer: true,
        contextRequiresEntity: false,
        skippedWhenMissingContext: true,
    },
    {
        type: "SetWorldProperty",
        fields: [
            { name: "target", kind: "string", required: false, minLength: 1 },
            {
                name: "propertyKey",
                kind: "enum",
                required: true,
                values: ["Width", "Height", "Depth", "Scale", "Position", "Rotation", "Visible", "Transparency", "CanCollide", "Anchored", "Color"],
            },
            { name: "value", kind: "any", required: false },
        ],
        outputHandles: [HANDLE_SUCCESS, HANDLE_FAILED],
        isBlocking: false,
        contextRequiresPlayer: false,
        contextRequiresEntity: false,
        skippedWhenMissingContext: false,
        dataInputs: [{ name: "value", kind: "any" }],
    },
    {
        type: "TweenWorldProperty",
        fields: [
            { name: "target", kind: "string", required: false, minLength: 1 },
            {
                name: "propertyKey",
                kind: "enum",
                required: true,
                values: ["Width", "Height", "Depth", "Scale", "Position", "Rotation", "Visible", "Transparency", "CanCollide", "Anchored", "Color"],
            },
            { name: "value", kind: "any", required: false },
            { name: "duration", kind: "number", required: false, min: 0 },
            { name: "delay", kind: "number", required: false, min: 0 },
            { name: "easing", kind: "enum", required: false, values: ["linear", "quad-out", "quad-in-out", "back-out", "elastic-out"] },
        ],
        outputHandles: [HANDLE_SUCCESS, HANDLE_FAILED],
        isBlocking: false,
        contextRequiresPlayer: false,
        contextRequiresEntity: false,
        skippedWhenMissingContext: false,
        dataInputs: [{ name: "value", kind: "any" }],
    },
    {
        type: "SetGoal",
        fields: [
            { name: "targetItemId", kind: "string", required: true, refEntity: "items" },
        ],
        outputHandles: NEXT_ONLY,
        isBlocking: true,
        contextRequiresPlayer: true,
        contextRequiresEntity: false,
        skippedWhenMissingContext: true,
    },
    {
        type: "SetDailyQuest",
        fields: [],
        outputHandles: NEXT_ONLY,
        isBlocking: false,
        contextRequiresPlayer: true,
        contextRequiresEntity: false,
        skippedWhenMissingContext: true,
    },
    {
        type: "SetJournalQuest",
        fields: [],
        outputHandles: NEXT_ONLY,
        isBlocking: false,
        contextRequiresPlayer: true,
        contextRequiresEntity: false,
        skippedWhenMissingContext: true,
    },
    {
        type: "GiveQuest",
        fields: [
            { name: "questIds", kind: "array", required: true, minLength: 1 },
        ],
        outputHandles: [HANDLE_NEXT, HANDLE_IN_PROGRESS, HANDLE_SUCCEEDED, HANDLE_FAILED, HANDLE_ERROR],
        isBlocking: false,
        contextRequiresPlayer: true,
        contextRequiresEntity: false,
        skippedWhenMissingContext: true,
    },
    {
        type: "CheckQuestStatus",
        fields: [
            { name: "questIds", kind: "array", required: true, minLength: 1 },
        ],
        outputHandles: [HANDLE_IN_PROGRESS, HANDLE_SUCCEEDED, HANDLE_FAILED, HANDLE_NOT_STARTED],
        isBlocking: false,
        contextRequiresPlayer: true,
        contextRequiresEntity: true,
        skippedWhenMissingContext: true,
    },
    {
        type: "ClaimQuest",
        fields: [],
        outputHandles: [HANDLE_SUCCESS, HANDLE_FAILED],
        isBlocking: false,
        contextRequiresPlayer: true,
        contextRequiresEntity: false,
        skippedWhenMissingContext: true,
    },
    {
        type: "CheckoutQuest",
        fields: [],
        outputHandles: [HANDLE_SUCCESS, HANDLE_FAILED],
        isBlocking: true,
        contextRequiresPlayer: true,
        contextRequiresEntity: false,
        skippedWhenMissingContext: true,
    },
    {
        type: "ClearQuestState",
        fields: [],
        outputHandles: NEXT_ONLY,
        isBlocking: false,
        contextRequiresPlayer: true,
        contextRequiresEntity: false,
        skippedWhenMissingContext: true,
    },
    {
        type: "Branch",
        fields: [
            { name: "logicalOperator", kind: "string", required: false },
            { name: "conditions", kind: "array", required: false },
        ],
        outputHandles: [HANDLE_TRUE, HANDLE_FALSE],
        isBlocking: false,
        contextRequiresPlayer: false,
        contextRequiresEntity: false,
        skippedWhenMissingContext: false,
        dataInputs: [{ name: "condition", kind: "any" }],
    },
    {
        type: "Compare",
        fields: [
            { name: "operator", kind: "enum", required: true, values: COMPARE_OPS },
        ],
        outputHandles: [HANDLE_TRUE, HANDLE_FALSE],
        isBlocking: false,
        contextRequiresPlayer: false,
        contextRequiresEntity: false,
        skippedWhenMissingContext: false,
        dataInputs: [
            { name: "a", kind: "any" },
            { name: "b", kind: "any" },
        ],
        dataOutputs: [
            { name: "value", kind: "boolean" },
        ],
    },
    {
        type: "WaitUntil",
        fields: [
            { name: "logicalOperator", kind: "string", required: true },
            { name: "conditions", kind: "array", required: true, minLength: 1 },
            { name: "timeoutSeconds", kind: "number", required: false },
        ],
        outputHandles: NEXT_ONLY,
        isBlocking: true,
        contextRequiresPlayer: true,
        contextRequiresEntity: false,
        skippedWhenMissingContext: false,
    },
    {

        type: "Switch",
        fields: [
            { name: "cases", kind: "array", required: true, minLength: 1 },
        ],
        outputHandles: [],
        isBlocking: false,
        contextRequiresPlayer: false,
        contextRequiresEntity: false,
        skippedWhenMissingContext: false,
        dataInputs: [{ name: "value", kind: "any" }],

        dataOutputs: [{ name: "Default", kind: "any" }],
    },
    {
        type: "Choice",
        fields: [
            { name: "prompt", kind: "string", required: true, minLength: 1 },
            {
                name: "cases",
                kind: "array",
                required: true,
                minLength: 1,
                description: "Array of response cases: { label: string, handle: string }.",
            },
            { name: "speaker", kind: "enum", required: false, values: ["Player", "Entity"] },
        ],
        outputHandles: ["Default"],
        isBlocking: true,
        contextRequiresPlayer: true,
        contextRequiresEntity: false,
        skippedWhenMissingContext: true,
    },
    {
        type: "PlaySound",
        fields: [
            { name: "soundPath", kind: "string", required: false, minLength: 1 },
        ],
        outputHandles: NEXT_ONLY,
        isBlocking: false,
        contextRequiresPlayer: false,
        contextRequiresEntity: true,
        skippedWhenMissingContext: true,
    },
    {
        type: "PlayAnimation",
        fields: [
            { name: "target", kind: "string", required: true, minLength: 1 },
            { name: "animationPath", kind: "string", required: false, minLength: 1 },
        ],
        outputHandles: [HANDLE_ON_START, HANDLE_ON_END, HANDLE_ON_EVENT],
        isBlocking: false,
        contextRequiresPlayer: false,
        contextRequiresEntity: false,
        skippedWhenMissingContext: true,
    },
    {
        type: "ShowTitle",
        fields: [
            { name: "text", kind: "string", required: true, minLength: 1 },
        ],
        outputHandles: NEXT_ONLY,
        isBlocking: false,
        contextRequiresPlayer: false,
        contextRequiresEntity: true,
        skippedWhenMissingContext: true,
    },
    {
        type: "DisplayInterface",
        fields: [
            { name: "interfaceName", kind: "string", required: false },
        ],
        outputHandles: NEXT_ONLY,
        isBlocking: false,
        contextRequiresPlayer: true,
        contextRequiresEntity: false,
        skippedWhenMissingContext: true,
        dataInputs: [{ name: "interfaceName", kind: "string" }],
    },
    {
        type: "OpenSession",
        fields: [
            { name: "sessionName", kind: "string", required: true, minLength: 1 },
        ],
        outputHandles: NEXT_ONLY,
        isBlocking: false,
        contextRequiresPlayer: true,
        contextRequiresEntity: false,
        skippedWhenMissingContext: true,
    },
    {
        type: "Attack",
        fields: [],
        outputHandles: NEXT_ONLY,
        isBlocking: false,
        contextRequiresPlayer: true,
        contextRequiresEntity: false,
        skippedWhenMissingContext: true,
    },
    {
        type: "Countdown",
        fields: [],
        outputHandles: ["Tick", "Finish"],
        isBlocking: false,
        contextRequiresPlayer: false,
        contextRequiresEntity: false,
        skippedWhenMissingContext: false,
        dataInputs: [{ name: "duration", kind: "number" }],
        dataOutputs: [
            { name: "secondsRemaining", kind: "number" },
        ],
    },
    {
        type: "CollectTimer",
        fields: [],
        outputHandles: NEXT_ONLY,
        isBlocking: false,
        contextRequiresPlayer: true,
        contextRequiresEntity: false,
        skippedWhenMissingContext: true,
        dataInputs: [
            { name: "instanceId", kind: "string" },
            { name: "recipeId", kind: "string" },
        ],
        selectionInputs: [
            { name: "instanceId", kind: "string", maxLength: 64 },
            { name: "recipeId", kind: "string", refEntity: "recipes", maxLength: 64 },
        ],
    },
    {
        type: "HideInterface",
        fields: [
            { name: "interfaceName", kind: "string", required: false },
        ],
        outputHandles: NEXT_ONLY,
        isBlocking: false,
        contextRequiresPlayer: true,
        contextRequiresEntity: false,
        skippedWhenMissingContext: true,
        dataInputs: [{ name: "interfaceName", kind: "string" }],
    },

    {
        type: "SetUiProperty",
        fields: [
            { name: "nodeId", kind: "string", required: true, minLength: 1 },
            {
                name: "propertyKey",
                kind: "enum",
                required: true,
                values: UI_PROPERTY_KEYS,
            },
        ],
        outputHandles: NEXT_ONLY,
        isBlocking: false,
        contextRequiresPlayer: true,
        contextRequiresEntity: false,
        skippedWhenMissingContext: true,

        dataInputs: [
            { name: "value", kind: "any" },
            { name: "gate", kind: "any" },
        ],
    },

    {
        type: "TweenProperty",
        fields: [
            { name: "nodeId", kind: "string", required: true, minLength: 1 },
            {
                name: "propertyKey",
                kind: "enum",
                required: true,
                values: UI_PROPERTY_KEYS,
            },
            { name: "duration", kind: "number", required: false, min: 0 },
            { name: "delay", kind: "number", required: false, min: 0 },
            {
                name: "easing",
                kind: "enum",
                required: false,
                values: ["linear", "quad-out", "quad-in-out", "back-out", "elastic-out"] as const,
            },
        ],
        outputHandles: NEXT_ONLY,
        isBlocking: false,
        contextRequiresPlayer: true,
        contextRequiresEntity: false,
        skippedWhenMissingContext: true,

        dataInputs: [
            { name: "value", kind: "any" },
            { name: "gate", kind: "any" },
        ],
    },

    {
        type: "ForEach",
        fields: [
            { name: "list", kind: "array", required: false },
            { name: "virtualized", kind: "boolean", required: false },
            { name: "virtualItemHeight", kind: "number", required: false, gt: 0 },
            { name: "virtualItemWidth", kind: "number", required: false, gt: 0 },
            { name: "virtualBuffer", kind: "number", required: false, min: 0 },
        ],
        outputHandles: ["Iterator", "Done"] as const,
        isBlocking: false,
        contextRequiresPlayer: false,
        contextRequiresEntity: false,
        skippedWhenMissingContext: false,
        dataInputs: [{ name: "list", kind: "list" }],
        dataOutputs: [
            { name: "item", kind: "any" },
            { name: "iterator", kind: "number" },
        ],
    },
    {
        type: "ForEachPlayer",
        fields: [
            { name: "scope", kind: "enum", required: false, values: ["AllConnected", "PlaceOwners", "MatchPlayers"] },
        ],
        outputHandles: NEXT_ONLY,
        isBlocking: false,
        contextRequiresPlayer: false,
        contextRequiresEntity: false,
        skippedWhenMissingContext: false,
    },
    {
        type: "AddChild",
        fields: [
            { name: "parentNodeId", kind: "string", required: true },
            { name: "templateName", kind: "string", required: true },
            { name: "scope", kind: "string", required: false },
        ],
        outputHandles: NEXT_ONLY,
        isBlocking: false,
        contextRequiresPlayer: true,
        contextRequiresEntity: false,
        skippedWhenMissingContext: true,
    },

    {
        type: "TransformRecipeItem",
        fields: [],
        outputHandles: ["Success", "Cancelled"],
        isBlocking: true,
        contextRequiresPlayer: true,
        contextRequiresEntity: false,
        skippedWhenMissingContext: true,
        dataInputs: [{ name: "recipeId", kind: "string" }],
        selectionInputs: [{ name: "recipeId", kind: "string", maxLength: 64, refEntity: "recipes" }],
    },
    {
        type: "SelectChoice",
        fields: [],
        outputHandles: NEXT_ONLY,
        isBlocking: false,
        contextRequiresPlayer: true,
        contextRequiresEntity: false,
        skippedWhenMissingContext: true,
        dataInputs: [
            { name: "case", kind: "any" },
            { name: "handle", kind: "string" },
        ],
        selectionInputs: [{ name: "handle", kind: "string", maxLength: 64 }],
    },
    {
        type: "TransformFreeModeSlots",
        fields: [],
        outputHandles: ["Success", "Cancelled"],
        isBlocking: true,
        contextRequiresPlayer: true,
        contextRequiresEntity: false,
        skippedWhenMissingContext: true,
        dataInputs: [{ name: "slots", kind: "list" }],
        selectionInputs: [{ name: "slots", kind: "string[]", refEntity: "items", maxItems: 16 }],
    },
    {
        type: "DeliverQuestItems",
        fields: [],
        outputHandles: ["Success", "Cancelled"],
        isBlocking: true,
        contextRequiresPlayer: true,
        contextRequiresEntity: false,
        skippedWhenMissingContext: true,
        dataInputs: [
            { name: "slotInstanceIds", kind: "list" },
            { name: "slots", kind: "list" },
        ],
        selectionInputs: [
            { name: "slotInstanceIds", kind: "string[]", maxItems: 64, maxLength: 64 },
        ],
    },
    {
        type: "SelectTargetItem",
        fields: [],
        outputHandles: NEXT_ONLY,
        isBlocking: false,
        contextRequiresPlayer: true,
        contextRequiresEntity: false,
        skippedWhenMissingContext: true,
        dataInputs: [
            { name: "instanceId", kind: "string" },
            { name: "recipeId", kind: "string" },
        ],
        selectionInputs: [
            { name: "instanceId", kind: "string", maxLength: 64 },
            { name: "recipeId", kind: "string", refEntity: "recipes", maxLength: 64 },
        ],
    },
    {
        type: "ClaimQuestById",
        fields: [],
        outputHandles: NEXT_ONLY,
        isBlocking: false,
        contextRequiresPlayer: true,
        contextRequiresEntity: false,
        skippedWhenMissingContext: true,
        dataInputs: [{ name: "questId", kind: "string" }],
        selectionInputs: [
            { name: "questId", kind: "string", refEntity: "quests", maxLength: 64 },
        ],
    },
    {
        type: "SetQuestGoal",
        fields: [],
        outputHandles: NEXT_ONLY,
        isBlocking: false,
        contextRequiresPlayer: true,
        contextRequiresEntity: false,
        skippedWhenMissingContext: true,
        dataInputs: [{ name: "questId", kind: "string" }],
        selectionInputs: [
            { name: "questId", kind: "string", refEntity: "quests", maxLength: 64 },
        ],
    },
    {
        type: "UseInventoryItem",
        fields: [],
        outputHandles: NEXT_ONLY,
        isBlocking: false,
        contextRequiresPlayer: true,
        contextRequiresEntity: false,
        skippedWhenMissingContext: true,
        dataInputs: [
            { name: "instanceId", kind: "string" },
            { name: "itemId", kind: "string" },
        ],
        selectionInputs: [
            { name: "instanceId", kind: "string", maxLength: 64, allowEmpty: true },
            { name: "itemId", kind: "string", refEntity: "items", maxLength: 64, allowEmpty: true },
        ],
    },

    {
        type: "SetState",
        fields: [
            { name: "stateKey", kind: "string", required: false },
        ],
        outputHandles: NEXT_ONLY,
        isBlocking: false,
        contextRequiresPlayer: true,
        contextRequiresEntity: false,
        skippedWhenMissingContext: true,
        dataInputs: [
            { name: "stateKey", kind: "string" },
            { name: "value", kind: "any" },
        ],
    },
    {
        type: "AppendState",
        fields: [
            { name: "stateKey", kind: "string", required: true, minLength: 1 },
        ],
        outputHandles: NEXT_ONLY,
        isBlocking: false,
        contextRequiresPlayer: true,
        contextRequiresEntity: false,
        skippedWhenMissingContext: true,
        dataInputs: [
            { name: "value", kind: "any" },
            { name: "max", kind: "number" },
        ],
    },
    {
        type: "RemoveState",
        fields: [
            { name: "stateKey", kind: "string", required: true, minLength: 1 },
        ],
        outputHandles: NEXT_ONLY,
        isBlocking: false,
        contextRequiresPlayer: true,
        contextRequiresEntity: false,
        skippedWhenMissingContext: true,
        dataInputs: [{ name: "value", kind: "any" }],
    },
];


const DATA_PRODUCER_SPECS: ActionSpec[] = [
    {
        type: "Get",
        fields: [

            { name: "query", kind: "object", required: true },
        ],
        outputHandles: [],
        isBlocking: false,
        contextRequiresPlayer: false,
        contextRequiresEntity: false,
        skippedWhenMissingContext: false,
        dataInputs: [
            { name: "arg", kind: "any" },
        ],
        dataOutputs: GET_OUTPUTS_ALL,
    },
    {
        type: "Math",
        fields: [
            { name: "operator", kind: "enum", required: true, values: MATH_OPS },
        ],
        outputHandles: [],
        isBlocking: false,
        contextRequiresPlayer: false,
        contextRequiresEntity: false,
        skippedWhenMissingContext: false,

        dataInputs: [
            { name: "a", kind: "number" },
            { name: "b", kind: "number" },
        ],
        dataOutputs: VALUE_PORT_NUMBER,
    },
    {
        type: "Concat",
        fields: [
            { name: "parts", kind: "array", required: true, minLength: 1 },
        ],
        outputHandles: [],
        isBlocking: false,
        contextRequiresPlayer: false,
        contextRequiresEntity: false,
        skippedWhenMissingContext: false,
        dataInputs: [],
        dataOutputs: VALUE_PORT_STRING,
    },

    {
        type: "Filter",
        fields: [
            { name: "field", kind: "string", required: false },
            {
                name: "op",
                kind: "enum",
                required: true,
                values: ["==", "!=", ">=", "<=", ">", "<"],
            },
        ],
        outputHandles: [],
        isBlocking: false,
        contextRequiresPlayer: false,
        contextRequiresEntity: false,
        skippedWhenMissingContext: false,
        dataInputs: [
            { name: "source", kind: "list" },
            { name: "value", kind: "any" },
        ],
        dataOutputs: [
            { name: "items", kind: "list" },
            { name: "count", kind: "number" },
        ],
    },

    {
        type: "Group",
        fields: [
            { name: "by", kind: "string", required: false },
        ],
        outputHandles: [],
        isBlocking: false,
        contextRequiresPlayer: false,
        contextRequiresEntity: false,
        skippedWhenMissingContext: false,
        dataInputs: [{ name: "source", kind: "list" }],
        dataOutputs: [
            { name: "items", kind: "list" },
            { name: "count", kind: "number" },
        ],
    },

    {
        type: "Flatten",
        fields: [
            { name: "by", kind: "string", required: false },
        ],
        outputHandles: [],
        isBlocking: false,
        contextRequiresPlayer: false,
        contextRequiresEntity: false,
        skippedWhenMissingContext: false,
        dataInputs: [{ name: "source", kind: "list" }],
        dataOutputs: [
            { name: "items", kind: "list" },
            { name: "count", kind: "number" },
        ],
    },

    {
        type: "ResolveItem",
        fields: [],
        outputHandles: [],
        isBlocking: false,
        contextRequiresPlayer: false,
        contextRequiresEntity: false,
        skippedWhenMissingContext: false,
        dataInputs: [{ name: "itemId", kind: "string" }],
        dataOutputs: [
            { name: "id", kind: "string" },
            { name: "name", kind: "string" },
            { name: "description", kind: "string" },
            { name: "category", kind: "string" },
        ],
    },
    {
        type: "GetAllProductions",
        fields: [],
        outputHandles: [],
        isBlocking: false,
        contextRequiresPlayer: true,
        contextRequiresEntity: false,
        skippedWhenMissingContext: true,
        dataOutputs: [
            { name: "items", kind: "list" },
            { name: "totals", kind: "list" },
            { name: "count", kind: "number" },
        ],
    },
];

ACTION_SPECS.push(...DATA_PRODUCER_SPECS);

export const ACTION_SPEC_BY_TYPE = new Map<ActionType, ActionSpec>(
    ACTION_SPECS.map((s) => [s.type, s]),
);


export const isDataProducer = (spec: ActionSpec): boolean =>
    spec.outputHandles.length === 0 && (spec.dataOutputs?.length ?? 0) > 0;

export const getActionSpec = (type: ActionType): ActionSpec | undefined =>
    ACTION_SPEC_BY_TYPE.get(type);

export const getEventSpec = (type: EventType): EventSpec | undefined =>
    EVENT_SPEC_BY_TYPE.get(type);

export const getOutputHandles = (
    type: ActionType | EventType,
): readonly string[] | undefined => {
    const action = ACTION_SPEC_BY_TYPE.get(type as ActionType);
    if (action) return action.outputHandles;
    const event = EVENT_SPEC_BY_TYPE.get(type as EventType);
    if (event) return event.outputHandles;
    return undefined;
};
